/**
 * Lazy-loaded icons with encapsulated Suspense boundaries.
 */
import React, { Suspense } from 'react';

// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazyForPaint } from '@atlassian/react-loosely-lazy';

import { withBrandIcon, type BrandIcon } from '../../../icons/with-brand-icon';

const OfficialIcon = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "@atlassian/logo-third-party/microsoft-onedrive" */ '@atlassian/logo-third-party/microsoft-onedrive'
		).then((m) => m.MicrosoftOneDriveIcon),
	{ moduleId: '@atlassian/logo-third-party/microsoft-onedrive' },
);

const LazyOneDriveIconComponent = lazyForPaint(
	() => import(/* webpackChunkName: "icon-onedrive" */ './utils').then((m) => m.OneDriveIcon),
	{ moduleId: '@atlassian/rovo-connectors-config/onedrive/icon' },
);

const LazyOneDriveSearchResultIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-onedrive-search-result" */ './utils').then(
			(m) => m.OneDriveSearchResultIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/onedrive/search-result-icon' },
);

const LazyOneDriveIconBorderlessComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-onedrive-borderless" */ './utils').then(
			(m) => m.OneDriveIconBorderless,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/onedrive/icon-borderless' },
);

/**
 * Exported icons with self-contained Suspense boundaries.
 * Consumers can use these directly without wrapping in LazySuspense.
 */
export const OneDriveIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyOneDriveIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'small',
);

export const OneDriveSearchResultIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyOneDriveSearchResultIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'medium',
);

export const OneDriveIconBorderless: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyOneDriveIconBorderlessComponent />
		</Suspense>
	),
	OfficialIcon,
	'xxsmall',
);
